/** Pickers for references: animals, places, groups, people, products, lots, equipment, jobs, crew. */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ANIMAL_CLASS, ANIMAL_STATUS, type AnimalSummary, type RefTarget, type SearchHit } from "../../contract";
import { api, qs } from "../lib/api";
import { t } from "../lib/i18n";
import { useApi } from "../lib/query";
import { Chip, Icon, TagLabel, cx } from "../ui/kit";

interface Option { id: string; label: string; meta?: string; tag?: string | null }

const ICON: Record<RefTarget, string> = {
  animal: "animal", group: "group", place: "place", contact: "contact", stock_item: "stock", stock_lot: "stock", asset: "wrench", job: "work", member: "people", fin_doc: "receipt",
};

async function searchOptions(target: RefTarget, q: string, filter?: Record<string, string | string[]>): Promise<Option[]> {
  switch (target) {
    case "animal": {
      const status = (filter?.status as string) ?? "all";
      const r = await api.get<{ items: AnimalSummary[] }>(`/animals${qs({ q, status, sex: filter?.sex as string, limit: 30 })}`);
      return r.items.map((a) => ({ id: a.id, label: a.label, tag: a.tag, meta: [t(ANIMAL_CLASS[a.class as keyof typeof ANIMAL_CLASS] ?? a.class), a.location?.label, a.status !== "active" ? t(ANIMAL_STATUS[a.status as keyof typeof ANIMAL_STATUS] ?? a.status) : null].filter(Boolean).join(" · ") }));
    }
    case "member": {
      const r = await api.get<{ items: Array<{ id: string; name: string; role: string; active: boolean }> }>("/people");
      return r.items.filter((p) => p.active && p.name.toLowerCase().includes(q.toLowerCase())).map((p) => ({ id: p.id, label: p.name, meta: t(p.role) }));
    }
    case "stock_lot": {
      const items = await api.get<{ items: Array<{ id: string; name: string; unit: string; kind: string }> }>(`/stock-items${qs({ kind: filter?.kind as string })}`);
      const matched = items.items.filter((i) => i.name.toLowerCase().includes(q.toLowerCase())).slice(0, 8);
      const lots = await Promise.all(matched.map((i) => api.get<{ lots: Array<{ id: string; code: string | null; expires_at: string | null; on_hand: number }> }>(`/stock-items/${i.id}`).then((d) => d.lots.map((l) => ({ id: l.id, label: l.code ? `${i.name} · ${l.code}` : i.name, meta: `${l.on_hand} ${i.unit}${l.expires_at ? ` · ${t("exp.")} ${l.expires_at}` : ""}` })))));
      return lots.flat();
    }
    case "stock_item": {
      const r = await api.get<{ items: Array<{ id: string; name: string; kind: string; on_hand: number; unit: string }> }>(`/stock-items${qs({ kind: Array.isArray(filter?.kind) ? filter!.kind.join(",") : filter?.kind })}`);
      return r.items.filter((i) => i.name.toLowerCase().includes(q.toLowerCase())).map((i) => ({ id: i.id, label: i.name, meta: `${t(i.kind)} · ${i.on_hand} ${i.unit}` }));
    }
    case "job": {
      const r = await api.get<{ items: Array<{ id: string; title: string; status: string }> }>("/jobs");
      return r.items.filter((j) => j.title.toLowerCase().includes(q.toLowerCase())).map((j) => ({ id: j.id, label: j.title }));
    }
    case "fin_doc": {
      const r = await api.get<{ items: Array<{ id: string; memo: string | null; number: string | null; doc_date: string; kind: string }> }>("/fin-docs");
      return r.items.filter((d) => `${d.memo} ${d.number}`.toLowerCase().includes(q.toLowerCase())).slice(0, 30).map((d) => ({ id: d.id, label: d.memo ?? d.number ?? d.kind, meta: d.doc_date }));
    }
    default: {
      if (!q.trim()) {
        const path = { group: "/groups", place: "/places", contact: "/contacts", asset: "/assets" }[target as "group" | "place" | "contact" | "asset"];
        const r = await api.get<{ items: Array<{ id: string; name: string; kind?: string; head?: number }> }>(path);
        return r.items.slice(0, 40).map((x) => ({ id: x.id, label: x.name, meta: x.head !== undefined ? t("{{count}} head", { count: x.head }) : x.kind ? t(x.kind) : undefined }));
      }
      const r = await api.get<{ items: SearchHit[] }>(`/search${qs({ q, kinds: target, limit: 30 })}`);
      return r.items.map((h) => ({ id: h.id, label: h.title, meta: h.subtitle }));
    }
  }
}

/** Resolve labels for ids we already hold. */
export function useLabels(refs: Array<{ kind: string; id: string }>): Record<string, string> {
  const key = refs.map((r) => `${r.kind}:${r.id}`).sort().join(",");
  const [labels, setLabels] = useState<Record<string, string>>({});
  useEffect(() => {
    const real = refs.filter((r) => r.id && !r.id.startsWith("new:"));
    if (!real.length) return;
    let alive = true;
    api.post<Record<string, string>>("/labels", { refs: real }).then((l) => { if (alive) setLabels(l); }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return labels;
}

export function RefPicker({ target, value, onChange, multiple, filter, placeholder, labels: known = {}, id, autoFocus }: {
  target: RefTarget; value: string | string[] | null | undefined; onChange: (v: string | string[] | null) => void; multiple?: boolean;
  filter?: Record<string, string | string[]>; placeholder?: string; labels?: Record<string, string>; id?: string; autoFocus?: boolean;
}) {
  const ids = useMemo(() => (Array.isArray(value) ? value : value ? [value] : []), [value]);
  const fetched = useLabels(ids.map((i) => ({ kind: target, id: i })));
  const [picked, setPicked] = useState<Record<string, string>>({});
  const labels = { ...fetched, ...known, ...picked };
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Option[]>([]);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const listId = useId();
  useEffect(() => {
    if (!open) return;
    let alive = true;
    const handle = setTimeout(() => { searchOptions(target, q, filter).then((o) => { if (alive) { setOptions(o); setActive(0); } }).catch(() => {}); }, 150);
    return () => { alive = false; clearTimeout(handle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, open, target]);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const choose = (o: Option) => {
    if (multiple) onChange(ids.includes(o.id) ? ids.filter((x) => x !== o.id) : [...ids, o.id]);
    else { onChange(o.id); setOpen(false); }
    setPicked((p) => ({ ...p, [o.id]: o.label }));
    setQ("");
  };
  return (
    <div className="picker" ref={box}>
      {ids.length > 0 && (
        <div className="picker-chosen">
          {ids.map((i) => <Chip key={i} icon={ICON[target]} onRemove={() => onChange(multiple ? ids.filter((x) => x !== i) : null)}>{labels[i] ?? "…"}</Chip>)}
        </div>
      )}
      {(multiple || !ids.length) && (
        <input
          id={id} className="input" value={q} placeholder={placeholder ?? t("Search")} autoFocus={autoFocus} autoComplete="off"
          role="combobox" aria-expanded={open} aria-autocomplete="list" aria-controls={open ? listId : undefined} aria-activedescendant={open && options[active] ? `${listId}-${active}` : undefined}
          onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, options.length - 1)); }
            if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
            if (e.key === "Enter" && options[active]) { e.preventDefault(); choose(options[active]); }
          }}
        />
      )}
      {open && (multiple || !ids.length) && (
        <ul className="picker-options" role="listbox" id={listId}>
          {options.length === 0 && <li className="picker-empty">{t("No matches")}</li>}
          {options.map((o, i) => (
            <li key={o.id} id={`${listId}-${i}`} role="option" aria-selected={ids.includes(o.id)} className={cx("picker-option", i === active && "picker-active", ids.includes(o.id) && "picker-selected")}
              onMouseDown={(e) => { e.preventDefault(); choose(o); }} onMouseEnter={() => setActive(i)}>
              {o.tag ? <TagLabel tag={o.tag} size="sm" /> : <Icon name={ICON[target]} size={16} />}
              <span className="picker-label">{o.tag && o.label.startsWith(o.tag) ? o.label.slice(o.tag.length).trim() || "" : o.label}</span>
              {o.meta && <span className="picker-meta">{o.meta}</span>}
              {ids.includes(o.id) && <Icon name="check" size={16} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Lists crew for assignment pickers without a search round trip. */
export function useCrew() {
  return useApi<{ items: Array<{ id: string; name: string; role: string; active: boolean }> }>("/people");
}
